import { UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { RouterModule } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import {
  EmptyStateComponent,
  StatusLabelComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  extendSortingDataAccessor,
  ObservableDataSource,
  PipesModule,
  timestampSortingAccessor,
} from '@principle-theorem/ng-shared';
import { FundingRequest, Invoice } from '@principle-theorem/principle-core';
import {
  FundingRequestStatus,
  IBrand,
  IInvoice,
  IMohClaimFundingRequest,
  IPatient,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  HISTORY_DATE_FORMAT,
  INamedDocument,
  WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { MomentModule } from 'ngx-moment';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { MohClaimsService } from '../../../moh/moh-claims.service';
import { MohService } from '../../../moh/moh.service';
import { MohClaimApprovalService } from '../../../moh/moh-claim-approval.service';

@Component({
  selector: 'pr-moh-claims-table',
  imports: [
    NgMaterialModule,
    PipesModule,
    MomentModule,
    StatusLabelComponent,
    UpperCasePipe,
    RouterModule,
    EmptyStateComponent,
    LetDirective,
  ],
  templateUrl: './moh-claims-table.component.html',
  styleUrl: './moh-claims-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MohClaimsService, MohService, MohClaimApprovalService],
})
export class MohClaimsTableComponent {
  private _brand$ = new ReplaySubject<WithRef<IBrand>>(1);
  private _practice$ = new ReplaySubject<WithRef<IPractice>>(1);
  private _patient$ = new BehaviorSubject<WithRef<IPatient> | undefined>(
    undefined
  );
  readonly dateFormat = HISTORY_DATE_FORMAT;
  mohClaimsService = inject(MohClaimsService);
  mohService = inject(MohService);
  dataSource: ObservableDataSource<WithRef<IMohClaimFundingRequest>>;
  noClaims$: Observable<boolean>;
  displayedColumns$: Observable<string[]>;
  pageSizeOptions = [25, 50, 100];
  pageSize = 25;

  @Input()
  set brand(brand: WithRef<IBrand>) {
    if (brand) {
      this._brand$.next(brand);
    }
  }
  @Input()
  set practice(practice: WithRef<IPractice>) {
    if (practice) {
      this._practice$.next(practice);
    }
  }
  @Input()
  set patient(patient: WithRef<IPatient> | undefined) {
    this._patient$.next(patient);
  }

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  @ViewChild(MatPaginator)
  set tablePaginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  constructor() {
    this.dataSource = new ObservableDataSource(
      this.mohClaimsService.getMohClaims$(this._practice$, this._patient$)
    );
    this.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );
    this.noClaims$ = this.dataSource.filteredLength$.pipe(
      map((length) => length === 0)
    );
    this.displayedColumns$ = this._patient$.pipe(
      map((patient) =>
        compact([
          'status',
          'reference',
          'createdAt',
          'claimType',
          !patient ? 'patient' : undefined,
          'invoice',
          'actions',
        ])
      )
    );
  }

  getStatusColour(status: FundingRequestStatus): string {
    return FundingRequest.getStatusColour(status);
  }

  claimSubmitted(claim: WithRef<IMohClaimFundingRequest>): boolean {
    return FundingRequest.claimSubmitted(claim);
  }

  claimInDraft(claim: WithRef<IMohClaimFundingRequest>): boolean {
    return FundingRequest.claimInDraft(claim);
  }

  canCancel(claim: WithRef<IMohClaimFundingRequest>): boolean {
    return FundingRequest.canCancel(claim);
  }

  invoiceLink$(invoiceRef: DocumentReference<IInvoice>): Observable<string[]> {
    const patientRef = Invoice.patientRef({ ref: invoiceRef });

    return this._brand$.pipe(
      map((brand) => [
        '/',
        brand.slug,
        'patients',
        patientRef.id,
        'account',
        'invoices',
        invoiceRef.id,
      ])
    );
  }

  patientLink$(patient: INamedDocument<IPatient>): Observable<string[]> {
    return this._brand$.pipe(
      map((brand) => ['/', brand.slug, 'patients', patient.ref.id])
    );
  }

  private _sortingDataAccessor(
    data: WithRef<IMohClaimFundingRequest>,
    sortHeaderId: string
  ): string | number | undefined {
    switch (sortHeaderId) {
      case 'createdAt':
        return timestampSortingAccessor(data.createdAt);
      default:
        return;
    }
  }
}
